import { Fragment, useState, useEffect } from 'react';
import {
  HelpCircle, Plus, Trash2, Copy, Check, ChevronDown, ChevronUp,
  ExternalLink, AlertCircle, X, ArrowUp, ArrowDown, Upload, Download, Film, AlertTriangle, CornerDownRight,
} from 'lucide-react';
import Layout from '../components/Layout';
import TagCreator from '../components/TagCreator';
import SubmissionsPanel from '../components/SubmissionsPanel';
import { PageHeader, Button, Badge, EmptyState, Spinner, ErrorMessage } from '../components/ui';
import { quizzesApi, platformConnectionsApi } from '../lib/api';
import type { Quiz, QuizType, QuizQuestion, QuizAnswer, QuizResult, QuizLeadCapture } from '../types/quizzes';
import type { PlatformConnection } from '../types/vault';
import VideoTab from '../components/video-quiz/VideoTab';
import VideoStyleTab from '../components/video-quiz/VideoStyleTab';
import VideoEmbedPanel from '../components/video-quiz/VideoEmbedPanel';
import VideoInsightsPanel from '../components/video-quiz/VideoInsightsPanel';
import { VideoQuestionFields, VideoAnswerFields, UnmappedWarning } from '../components/video-quiz/VideoQuestionFields';
import { Switch } from '../components/video-quiz/controls';
import {
  adaptQuestionsForVideo, clearAsksTo, defaultVideoConfig, defaultVideoStyle, followUpCycles, isVideoQuestionType,
  orphanFollowUps, setFollowUp, sortByAt, unmappedQuestions,
} from '../lib/videoQuiz';

const QUESTION_TYPES = [
  { value: 'single_choice', label: 'Single Choice' },
  { value: 'multi_choice', label: 'Multi Choice' },
  { value: 'image_choice', label: 'Image Choice' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'text_input', label: 'Text Input' },
] as const;

const QUIZ_TYPE_INFO = [
  { value: 'personality', label: 'Personality', desc: 'Each answer maps to an outcome. Most selected outcome wins.' },
  { value: 'scored', label: 'Scored', desc: 'Answers have point values. Total score determines the result tier.' },
  { value: 'assessment', label: 'Assessment', desc: 'Mark correct answers. The number correct maps to a result tier (e.g. pass/fail, grade).' },
  { value: 'outcome', label: 'Outcome', desc: 'Answer paths lead directly to specific results.' },
  { value: 'survey', label: 'Survey', desc: 'Ungraded. No scoring and no results — just collect answers and show a thank-you screen.' },
] as const;

const FONT_OPTIONS = ['Inter', 'Roboto', 'Open Sans', 'Lato', 'Poppins', 'Arial', 'Georgia'];

function timeAgo(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function Quizzes() {
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [connections, setConnections] = useState<PlatformConnection[]>([]);
  const [newName, setNewName] = useState('');
  const [newConnId, setNewConnId] = useState('');
  const [newType, setNewType] = useState('personality');
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editQuiz, setEditQuiz] = useState<Quiz | null>(null);
  const [activeTab, setActiveTab] = useState<'questions' | 'video' | 'results' | 'lead' | 'style' | 'embed' | 'submissions'>('questions');
  // Video quizzes
  const [newVideo, setNewVideo] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  /** Whether the saved quiz has a video, so turning it off sends `video: null`. */
  const [savedHadVideo, setSavedHadVideo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [cfTags, setCfTags] = useState<Array<{ id: number; name: string; color: string }>>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [copiedPrompt, setCopiedPrompt] = useState(false);

  const loadQuizzes = async () => {
    try {
      const res = await quizzesApi.list();
      if (res.success && res.data) setQuizzes((res.data as unknown as { quizzes: Quiz[] }).quizzes || []);
    } catch { setError('Failed to load quizzes'); } finally { setLoading(false); }
  };
  const loadConnections = async () => {
    try {
      const res = await platformConnectionsApi.list();
      if (res.success && res.data) {
        const all = (res.data as unknown as { connections: PlatformConnection[] }).connections || [];
        setConnections(all.filter((c) => c.platform === 'clickfunnels' && c.status === 'active'));
      }
    } catch {}
  };
  useEffect(() => { loadQuizzes(); loadConnections(); }, []);

  const handleCreate = async () => {
    if (!newName.trim() || !newConnId) return;
    setCreating(true);
    setCreateError(null);
    try {
      const res = await quizzesApi.create(newName.trim(), newConnId, newType, newVideo ? defaultVideoConfig() : undefined);
      if (res.success) {
        setShowCreateModal(false); setNewName(''); setNewConnId(''); setNewType('personality'); setNewVideo(false);
        await loadQuizzes();
        const created = (res.data as unknown as { quiz?: Quiz } | undefined)?.quiz;
        if (created?.video) await handleExpand(created.id, 'video');
      }
      else setCreateError(res.message || 'Failed to create quiz');
    } catch { setCreateError('Failed to create quiz'); } finally { setCreating(false); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this quiz and all submissions?')) return;
    try { await quizzesApi.delete(id); setQuizzes((p) => p.filter((q) => q.id !== id)); if (expandedId === id) { setExpandedId(null); setEditQuiz(null); } } catch { setError('Failed to delete quiz'); }
  };

  const handleExpand = async (id: string, tab: typeof activeTab = 'questions') => {
    if (expandedId === id) { setExpandedId(null); setEditQuiz(null); return; }
    setExpandedId(id); setActiveTab(tab); setSaveError(null);
    try {
      const res = await quizzesApi.get(id);
      if (res.success && res.data) {
        const q = (res.data as unknown as { quiz: Quiz }).quiz;
        setEditQuiz(q);
        setSavedHadVideo(!!q.video);
      }
    } catch { setError('Failed to load quiz'); }
    setLoadingTags(true);
    try { const res = await quizzesApi.getTags(id); if (res.success && res.data) setCfTags((res.data as unknown as { tags: typeof cfTags }).tags || []); } catch {}
    setLoadingTags(false);
  };

  /** Saves the editor state (or the given quiz, e.g. straight after a video upload). Returns true on success. */
  const handleSave = async (override?: Quiz): Promise<boolean> => {
    const quiz = override ?? editQuiz;
    if (!quiz) return false; setSaving(true); setSaveError(null);
    try {
      // Video quizzes play questions in timeline order, so keep sortOrder in step with `at`.
      const questions = quiz.video
        ? sortByAt(quiz.questions).map((q, i) => ({ ...q, sortOrder: i }))
        : quiz.questions;
      const res = await quizzesApi.update(quiz.id, {
        name: quiz.name, quizType: quiz.quizType, questions,
        results: quiz.results, leadCapture: quiz.leadCapture,
        styleConfig: quiz.styleConfig, settings: quiz.settings,
        // Classic quizzes never send `video`; turning video off sends null.
        ...(quiz.video || savedHadVideo ? { video: quiz.video ?? null } : {}),
      });
      if (res.success && res.data) {
        const u = (res.data as unknown as { quiz: Quiz }).quiz;
        setEditQuiz(u); setQuizzes((p) => p.map((q) => q.id === u.id ? { ...q, name: u.name, quizType: u.quizType, video: u.video } : q));
        setSavedHadVideo(!!u.video);
        setPreviewKey((k) => k + 1);
        setSavedMsg(true); setTimeout(() => setSavedMsg(false), 2000);
        return true;
      }
      setSaveError(res.message || 'Failed to save');
      return false;
    } catch { setSaveError('Failed to save'); return false; } finally { setSaving(false); }
  };

  const toggleVideo = (on: boolean) => {
    if (!editQuiz) return;
    if (on) {
      setEditQuiz({
        ...editQuiz,
        video: editQuiz.video ?? defaultVideoConfig(),
        questions: adaptQuestionsForVideo(editQuiz.questions),
        styleConfig: { ...editQuiz.styleConfig, video: editQuiz.styleConfig.video ?? defaultVideoStyle() },
      });
    } else {
      if (!confirm('Turn this back into a classic quiz? The video, timeline and branch clips are removed when you save.')) return;
      setEditQuiz({ ...editQuiz, video: null });
      if (activeTab === 'video') setActiveTab('questions');
    }
  };

  const handleTogglePublish = async (quiz: Quiz) => {
    const s = quiz.status === 'published' ? 'draft' : 'published';
    try { const res = await quizzesApi.update(quiz.id, { status: s }); if (res.success) { loadQuizzes(); if (editQuiz?.id === quiz.id) setEditQuiz((p) => p ? { ...p, status: s as Quiz['status'] } : p); } } catch { setError('Failed to update status'); }
  };

  // Question helpers
  const addQuestion = () => {
    if (!editQuiz) return;
    const q: QuizQuestion = {
      id: crypto.randomUUID(), type: 'single_choice', title: 'New Question', required: true, sortOrder: editQuiz.questions.length,
      answers: [
        { id: crypto.randomUUID(), text: 'Option A', score: 1 },
        { id: crypto.randomUUID(), text: 'Option B', score: 0 },
      ],
    };
    if (editQuiz.video) {
      // Next free stop: 5 s after the last question.
      const lastAt = editQuiz.questions.reduce((m, x) => (x.followUp ? m : Math.max(m, x.at ?? 0)), 0);
      q.at = Math.round((lastAt + 5) * 10) / 10;
    }
    setEditQuiz({ ...editQuiz, questions: [...editQuiz.questions, q] });
  };

  const updateQuestion = (qId: string, updates: Partial<QuizQuestion>) => {
    if (!editQuiz) return;
    setEditQuiz({ ...editQuiz, questions: editQuiz.questions.map((q) => q.id === qId ? { ...q, ...updates } : q) });
  };

  const removeQuestion = (qId: string) => {
    if (!editQuiz) return;
    // Answers that asked a removed follow-up go back to "Continue".
    setEditQuiz({ ...editQuiz, questions: editQuiz.questions.filter((q) => q.id !== qId).map((q) => clearAsksTo(q, qId)) });
  };

  const moveQuestion = (qId: string, dir: 'up' | 'down') => {
    if (!editQuiz) return;
    const sorted = [...editQuiz.questions].sort((a, b) => a.sortOrder - b.sortOrder);
    const idx = sorted.findIndex((q) => q.id === qId);
    if ((dir === 'up' && idx === 0) || (dir === 'down' && idx === sorted.length - 1)) return;
    const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
    const tmp = sorted[idx].sortOrder;
    sorted[idx] = { ...sorted[idx], sortOrder: sorted[swapIdx].sortOrder };
    sorted[swapIdx] = { ...sorted[swapIdx], sortOrder: tmp };
    setEditQuiz({ ...editQuiz, questions: sorted });
  };

  // Answer helpers
  const addAnswer = (qId: string) => {
    if (!editQuiz) return;
    const a: QuizAnswer = { id: crypto.randomUUID(), text: 'New Option', score: 0 };
    updateQuestion(qId, { answers: [...(editQuiz.questions.find((q) => q.id === qId)?.answers || []), a] });
  };

  const updateAnswer = (qId: string, aId: string, updates: Partial<QuizAnswer>) => {
    if (!editQuiz) return;
    const q = editQuiz.questions.find((q) => q.id === qId);
    if (!q) return;
    updateQuestion(qId, { answers: q.answers.map((a) => a.id === aId ? { ...a, ...updates } : a) });
  };

  const removeAnswer = (qId: string, aId: string) => {
    if (!editQuiz) return;
    const q = editQuiz.questions.find((q) => q.id === qId);
    if (!q) return;
    updateQuestion(qId, { answers: q.answers.filter((a) => a.id !== aId) });
  };

  // Result helpers
  const addResult = () => {
    if (!editQuiz) return;
    const r: QuizResult = { id: crypto.randomUUID(), title: 'New Result', description: '', ctaText: '', ctaUrl: '', customAttributes: {} };
    setEditQuiz({ ...editQuiz, results: [...editQuiz.results, r] });
  };

  const updateResult = (rId: string, updates: Partial<QuizResult>) => {
    if (!editQuiz) return;
    setEditQuiz({ ...editQuiz, results: editQuiz.results.map((r) => r.id === rId ? { ...r, ...updates } : r) });
  };

  const removeResult = (rId: string) => {
    if (!editQuiz) return;
    setEditQuiz({ ...editQuiz, results: editQuiz.results.filter((r) => r.id !== rId) });
  };

  // Import processing
  const processImport = (mode: 'replace' | 'append') => {
    if (!editQuiz) return;
    setImportError(null);
    try {
      const data = JSON.parse(importJson);
      if (!data.questions || !Array.isArray(data.questions)) { setImportError('JSON must have a "questions" array'); return; }
      if (!data.results || !Array.isArray(data.results)) { setImportError('JSON must have a "results" array'); return; }

      // Generate result IDs and build outcome map
      const outcomeMap: Record<string, string> = {};
      const newResults: QuizResult[] = data.results.map((r: any) => {
        const id = crypto.randomUUID();
        outcomeMap[r.title] = id;
        return {
          id, title: r.title || 'Untitled Result',
          description: r.description || '',
          ctaText: r.ctaText || '', ctaUrl: r.ctaUrl || '',
          redirectOnResult: r.redirectOnResult || false,
          imageUrl: r.imageUrl, tagId: r.tagId,
          minScore: r.minScore, maxScore: r.maxScore,
          customAttributes: r.customAttributes || {},
        };
      });

      // Process questions
      const startOrder = mode === 'append' ? editQuiz.questions.length : 0;
      const newQuestions: QuizQuestion[] = data.questions.map((q: any, idx: number) => ({
        id: crypto.randomUUID(),
        type: q.type || 'single_choice',
        title: q.title || 'Untitled Question',
        description: q.description,
        imageUrl: q.imageUrl,
        required: q.required !== undefined ? q.required : true,
        sortOrder: startOrder + idx,
        answers: (q.answers || []).map((a: any) => ({
          id: crypto.randomUUID(),
          text: a.text || '',
          score: a.score || 0,
          isCorrect: a.isCorrect === true || undefined,
          imageUrl: a.imageUrl,
          outcomeId: a.outcome ? outcomeMap[a.outcome] : a.outcomeId,
        })),
      }));

      if (mode === 'replace') {
        setEditQuiz({ ...editQuiz, questions: newQuestions, results: newResults });
      } else {
        // Merge results (avoid duplicates by title)
        const existingTitles = new Set(editQuiz.results.map((r) => r.title));
        const mergedResults = [...editQuiz.results, ...newResults.filter((r) => !existingTitles.has(r.title))];
        // Remap outcomeIds for appended questions to use existing result IDs where titles match
        const fullOutcomeMap: Record<string, string> = {};
        mergedResults.forEach((r) => { fullOutcomeMap[r.title] = r.id; });
        const remappedQuestions = newQuestions.map((q) => ({
          ...q, answers: q.answers.map((a) => {
            if (a.outcomeId) {
              const resultTitle = data.results.find((r: any) => outcomeMap[r.title] === a.outcomeId)?.title;
              if (resultTitle && fullOutcomeMap[resultTitle]) return { ...a, outcomeId: fullOutcomeMap[resultTitle] };
            }
            return a;
          }),
        }));
        setEditQuiz({ ...editQuiz, questions: [...editQuiz.questions, ...remappedQuestions], results: mergedResults });
      }

      setShowImportModal(false);
      setImportJson('');
    } catch (e) {
      setImportError(e instanceof SyntaxError ? 'Invalid JSON. Please check the format.' : String(e));
    }
  };

  // Export handler
  const handleExport = () => {
    if (!editQuiz) return;
    const outcomeIdToTitle: Record<string, string> = {};
    editQuiz.results.forEach((r) => { outcomeIdToTitle[r.id] = r.title; });

    const exportData = {
      questions: [...editQuiz.questions].sort((a, b) => a.sortOrder - b.sortOrder).map((q) => ({
        type: q.type, title: q.title,
        ...(q.description ? { description: q.description } : {}),
        answers: q.answers.map((a) => ({
          text: a.text, score: a.score,
          ...(a.isCorrect ? { isCorrect: true } : {}),
          ...(a.outcomeId && outcomeIdToTitle[a.outcomeId] ? { outcome: outcomeIdToTitle[a.outcomeId] } : {}),
          ...(a.imageUrl ? { imageUrl: a.imageUrl } : {}),
        })),
      })),
      results: editQuiz.results.map((r) => ({
        title: r.title, description: r.description,
        ...(r.ctaText ? { ctaText: r.ctaText } : {}),
        ...(r.ctaUrl ? { ctaUrl: r.ctaUrl } : {}),
        ...(r.redirectOnResult ? { redirectOnResult: true } : {}),
        ...(r.minScore !== undefined ? { minScore: r.minScore } : {}),
        ...(r.maxScore !== undefined ? { maxScore: r.maxScore } : {}),
        ...(r.imageUrl ? { imageUrl: r.imageUrl } : {}),
        ...(Object.keys(r.customAttributes || {}).length > 0 ? { customAttributes: r.customAttributes } : {}),
      })),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${editQuiz.slug}-quiz.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const getAIPrompt = () => {
    const type = editQuiz?.quizType || 'personality';
    return `Create a ${type} quiz with 5-8 questions about [YOUR TOPIC HERE].

Format as JSON with this exact structure:
{
  "questions": [
    {
      "type": "single_choice",
      "title": "Question text here",
      "answers": [
        { "text": "Answer A", "score": 1, "outcome": "Result Title A" },
        { "text": "Answer B", "score": 1, "outcome": "Result Title B" },
        { "text": "Answer C", "score": 1, "outcome": "Result Title C" }
      ]
    }
  ],
  "results": [
    {
      "title": "Result Title A",
      "description": "Description shown to the quiz taker (2-3 sentences)",
      "ctaUrl": "https://example.com"
    }
  ]
}

Rules:
- Each question needs 2-4 answers
- Question types: single_choice, multi_choice, yes_no${type === 'personality' ? '\n- Each answer "outcome" must match a result "title" exactly' : ''}${type === 'scored' ? '\n- Set "score" as points (0-3) on each answer\n- Add "minScore" and "maxScore" on each result to define score ranges' : ''}${type === 'assessment' ? '\n- Mark the correct answer(s) with "isCorrect": true (omit on wrong answers)\n- The score is the NUMBER of correct answers chosen\n- Add "minScore" and "maxScore" on each result for the number-correct range (e.g. 0-2, 3-5)' : ''}${type === 'outcome' ? '\n- Each answer "outcome" must match a result "title" exactly\n- The last question\'s answer determines the final result' : ''}${type === 'survey' ? '\n- This is an UNGRADED survey: do NOT add "outcome", "score" or "isCorrect" to any answer\n- Return "results": [] (an empty array) — surveys have no results\n- Include a mix of choice questions and open-ended "text_input" questions' : ''}
- Make it engaging and conversational
${type === 'survey' ? '- Ask questions worth analysing later; free-text answers are captured in full' : '- Create 3-5 distinct results\n- "ctaUrl" is optional. If a result has a ctaUrl, the taker is automatically redirected there; leave it out to show the in-quiz result page (title + description).'}
- Return ONLY the JSON, no explanation`;
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><Spinner /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <PageHeader
          eyebrow="Lead capture"
          title="Quizzes"
          description="Create embeddable quizzes that sync results to ClickFunnels"
          actions={<Button icon={Plus} onClick={() => setShowCreateModal(true)}>Create Quiz</Button>}
        />

        {error && (
          <div className="mb-4 p-3 bg-danger-soft border border-line rounded-[11px] flex items-center gap-2 text-danger text-sm">
            <AlertCircle className="w-4 h-4" /> <ErrorMessage message={error} />
            <button onClick={() => setError(null)} className="ml-auto text-ink-3 hover:text-danger"><X className="w-4 h-4" /></button>
          </div>
        )}

        {quizzes.length === 0 ? (
          <EmptyState
            icon={HelpCircle}
            title="No quizzes yet"
            description="Create your first quiz."
            action={<Button icon={Plus} onClick={() => setShowCreateModal(true)}>Create Quiz</Button>}
          />
        ) : (
          <div className="space-y-3">
            {quizzes.map((quiz) => (
              <div key={quiz.id} className="ds-card overflow-hidden hover:-translate-y-0.5 hover:shadow-card transition-all">
                <div className="flex items-center px-3 md:px-5 py-3 md:py-4 cursor-pointer hover:bg-surface-2" onClick={() => handleExpand(quiz.id)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
                      <span className="font-medium text-ink truncate">{quiz.name}</span>
                      <Badge tone={quiz.status === 'published' ? 'success' : 'neutral'}>{quiz.status}</Badge>
                      <Badge tone="accent">{quiz.quizType}</Badge>
                      {(quiz.video || quiz.isVideo) && <Badge tone="neutral"><Film className="w-3 h-3" /> video</Badge>}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-ink-3">
                      <span className="truncate font-mono">{quiz.video || quiz.isVideo ? `video · ${quiz.slug}` : `/q/${quiz.slug}`}</span>
                      {quiz.connectionName && <span className="hidden sm:inline">via {quiz.connectionName}</span>}
                      {!(quiz.video || quiz.isVideo) && <span>{quiz.submissionCount || 0} subs</span>}
                      <span>{timeAgo(quiz.createdAt)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button onClick={(e) => { e.stopPropagation(); handleTogglePublish(quiz); }} className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors" style={{ background: quiz.status === 'published' ? 'var(--success)' : 'var(--ink-3)' }}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${quiz.status === 'published' ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(quiz.id); }} className="p-1.5 text-ink-3 hover:text-danger"><Trash2 className="w-4 h-4" /></button>
                    {expandedId === quiz.id ? <ChevronUp className="w-4 h-4 text-ink-3" /> : <ChevronDown className="w-4 h-4 text-ink-3" />}
                  </div>
                </div>

                {expandedId === quiz.id && editQuiz && (() => {
                  // Surveys are ungraded: the Results tab becomes a Thank You tab,
                  // and the per-answer score/outcome/correct controls drop out.
                  const isSurvey = editQuiz.quizType === 'survey';
                  const isVideo = !!editQuiz.video;
                  const tabs = isVideo
                    ? (['questions', 'video', 'results', 'lead', 'style', 'embed', 'submissions'] as const)
                    : (['questions', 'results', 'lead', 'style', 'embed', 'submissions'] as const);
                  const unmapped = isVideo ? unmappedQuestions(editQuiz.questions) : [];
                  const orderedQuestions = isVideo ? sortByAt(editQuiz.questions) : [...editQuiz.questions].sort((a, b) => a.sortOrder - b.sortOrder);
                  const questionTypes = isVideo ? QUESTION_TYPES.filter((t) => isVideoQuestionType(t.value)) : QUESTION_TYPES;
                  // Follow-ups (video only) have no stop time; they're listed after the timed questions.
                  const followUpQuestions = isVideo ? orderedQuestions.filter((q) => q.followUp) : [];
                  const neverAskedIds = new Set(isVideo ? orphanFollowUps(editQuiz.questions).map((q) => q.id) : []);
                  const cycles = isVideo ? followUpCycles(editQuiz.questions) : [];
                  const firstFollowUpIdx = orderedQuestions.findIndex((q) => isVideo && q.followUp);
                  return (
                  <div className="border-t border-line">
                    <div className="flex border-b border-line px-3 md:px-5 overflow-x-auto">
                      {tabs.map((tab) => (
                        <button key={tab} onClick={() => setActiveTab(tab)}
                          className={`px-3 md:px-4 py-3 text-xs md:text-sm font-medium border-b-2 -mb-px capitalize whitespace-nowrap ${activeTab === tab ? 'border-accent text-accent-ink' : 'border-transparent text-ink-3 hover:text-ink-2'}`}>
                          {tab === 'lead' ? 'Lead Capture' : tab === 'results' && isSurvey ? 'Thank You' : tab === 'submissions' && isVideo ? 'Insights' : tab}
                        </button>
                      ))}
                    </div>

                    <div className="p-3 md:p-5">
                      {/* ── Questions Tab ── */}
                      {activeTab === 'questions' && (
                        <div>
                          <div className="flex justify-between items-center mb-4">
                            <div className="flex flex-wrap gap-3">
                              <div>
                                <label className="ds-label">Quiz Name</label>
                                <input type="text" value={editQuiz.name} onChange={(e) => setEditQuiz({ ...editQuiz, name: e.target.value })} className="ds-input w-full sm:w-64" />
                              </div>
                              <div>
                                <label className="ds-label">Type</label>
                                {/* Switching type never clears results[] — it just stops being used,
                                    so switching to Survey and back is lossless. */}
                                <select
                                  value={editQuiz.quizType}
                                  onChange={(e) => setEditQuiz({ ...editQuiz, quizType: e.target.value as QuizType })}
                                  className="ds-input w-full sm:w-44"
                                >
                                  {QUIZ_TYPE_INFO.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="ds-label">Video quiz</label>
                                <div className="flex items-center gap-2 h-[38px]">
                                  <Switch checked={isVideo} onChange={toggleVideo} label="Video quiz" />
                                  <span className="text-xs text-ink-3">{isVideo ? 'Questions pop up over a video' : 'Off'}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Button variant="ghost" icon={Upload} onClick={() => { setImportJson(''); setImportError(null); setShowImportModal(true); }}>Import</Button>
                              <Button variant="ghost" icon={Download} onClick={handleExport} disabled={editQuiz.questions.length === 0}>Export</Button>
                              <button onClick={addQuestion} className="flex items-center gap-1 px-3 py-1.5 bg-accent-soft text-accent-ink rounded-[10px] text-sm font-medium hover:opacity-90">
                                <Plus className="w-4 h-4" /> Add Question
                              </button>
                            </div>
                          </div>

                          {isVideo && <UnmappedWarning questions={unmapped} />}
                          {isVideo && cycles.length > 0 && (
                            <div className="mb-4 p-3 bg-warning-soft border border-line rounded-lg text-sm">
                              <div className="flex items-center gap-2 font-medium text-warning"><AlertTriangle className="w-4 h-4" /> Follow-up questions loop back on themselves</div>
                              <p className="text-xs text-ink-2 mt-1 mb-1.5">A follow-up can't (directly or indirectly) ask itself. Change one of these answers' "Then" — the quiz won't save until the loop is gone.</p>
                              <ul className="list-disc ml-5 text-ink-2 text-xs space-y-0.5">
                                {cycles.map((loop) => <li key={loop.join('→')}>{loop.join(' → ')}</li>)}
                              </ul>
                            </div>
                          )}
                          {isVideo && editQuiz.questions.length > 0 && (
                            <p className="text-xs text-ink-3 mb-3">Questions are ordered by when they stop the video. Place them precisely on the Video tab's timeline. Follow-up questions have no stop time — an answer's "Then" menu asks them.</p>
                          )}

                          {editQuiz.questions.length === 0 ? (
                            <p className="text-ink-3 text-sm text-center py-8">No questions yet. Click "Add Question" to start building.</p>
                          ) : (
                            <div className="space-y-4">
                              {orderedQuestions.map((q, idx) => (
                                <Fragment key={q.id}>
                                {idx === firstFollowUpIdx && (
                                  <div className="flex items-center gap-2 pt-2 text-xs font-semibold text-ink-3 uppercase tracking-wide">
                                    <CornerDownRight className="w-3.5 h-3.5" /> Follow-up questions
                                    <span className="flex-1 border-t border-line" />
                                  </div>
                                )}
                                <div className={`border border-line rounded-lg p-3 md:p-4 bg-surface-2 overflow-hidden ${isVideo && q.followUp ? 'ml-4 md:ml-8 border-l-4 border-l-[var(--violet)]' : ''}`}>
                                  <div className="flex items-start gap-2 md:gap-3">
                                    <div className="flex flex-col items-center gap-1 pt-1 flex-shrink-0">
                                      {!isVideo && <button onClick={() => moveQuestion(q.id, 'up')} className="text-ink-3 hover:text-ink"><ArrowUp className="w-3.5 h-3.5" /></button>}
                                      <span className="text-xs font-bold text-ink-3">{idx + 1}</span>
                                      {!isVideo && <button onClick={() => moveQuestion(q.id, 'down')} className="text-ink-3 hover:text-ink"><ArrowDown className="w-3.5 h-3.5" /></button>}
                                    </div>
                                    <div className="flex-1 min-w-0 space-y-3">
                                      {/* Question title + controls */}
                                      <div className="space-y-2">
                                        <input type="text" value={q.title} onChange={(e) => updateQuestion(q.id, { title: e.target.value })} placeholder="Question title" className="ds-input w-full" />
                                        <div className="flex flex-wrap items-center gap-2">
                                          <select value={q.type} onChange={(e) => {
                                            const type = e.target.value as QuizQuestion['type'];
                                            let answers = q.answers;
                                            if (type === 'yes_no') answers = [{ id: crypto.randomUUID(), text: 'Yes', score: 1 }, { id: crypto.randomUUID(), text: 'No', score: 0 }];
                                            else if (type === 'text_input') answers = [];
                                            updateQuestion(q.id, { type, answers });
                                          }} className="ds-input">
                                            {!questionTypes.some((t) => t.value === q.type) && <option value={q.type}>{q.type} (not supported in video)</option>}
                                            {questionTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                          </select>
                                          <label className="flex items-center gap-1 text-xs text-ink-3"><input type="checkbox" checked={q.required} onChange={(e) => updateQuestion(q.id, { required: e.target.checked })} className="rounded" /> Req</label>
                                          <button onClick={() => removeQuestion(q.id)} className="text-ink-3 hover:text-danger ml-auto"><Trash2 className="w-4 h-4" /></button>
                                        </div>
                                        {isVideo && (
                                          <VideoQuestionFields
                                            question={q}
                                            onChange={(patch) => updateQuestion(q.id, patch)}
                                            onToggleFollowUp={(on) => setEditQuiz((p) => (p ? { ...p, questions: setFollowUp(p.questions, q.id, on) } : p))}
                                            unmapped={unmapped.some((u) => u.id === q.id)}
                                            neverAsked={neverAskedIds.has(q.id)}
                                          />
                                        )}
                                      </div>

                                      {/* Answers */}
                                      {q.type !== 'text_input' && (
                                        <div className="space-y-2">
                                          {/* Header — hidden on mobile */}
                                          <div className="hidden md:flex items-center gap-2 text-xs text-ink-3 font-medium">
                                            <span className="flex-1">Answer</span>
                                            {editQuiz.quizType === 'scored' && <span className="w-16 text-center">Score</span>}
                                            {editQuiz.quizType === 'assessment' && <span className="w-20 text-center">Correct</span>}
                                            {(editQuiz.quizType === 'personality' || editQuiz.quizType === 'outcome') && <span className="w-36">Maps to Result</span>}
                                            <span className="w-6"></span>
                                          </div>
                                          {q.answers.map((a) => (
                                            <div key={a.id} className={`flex flex-wrap ${isVideo ? '' : 'md:flex-nowrap'} items-center gap-2 ${isVideo ? 'border-b border-line pb-2 last:border-b-0' : ''}`}>
                                              <input type="text" value={a.text} onChange={(e) => updateAnswer(q.id, a.id, { text: e.target.value })} placeholder="Answer text" className="ds-input w-full md:flex-1" />
                                              {a.imageUrl !== undefined && q.type === 'image_choice' && (
                                                <input type="text" value={a.imageUrl || ''} onChange={(e) => updateAnswer(q.id, a.id, { imageUrl: e.target.value })} placeholder="Image URL" className="ds-input w-full md:w-40 text-xs" />
                                              )}
                                              {editQuiz.quizType === 'scored' && (
                                                <div className="flex items-center gap-1">
                                                  <span className="text-xs text-ink-3 md:hidden">Score:</span>
                                                  <input type="number" value={a.score} onChange={(e) => updateAnswer(q.id, a.id, { score: parseInt(e.target.value) || 0 })} className="ds-input w-16 text-center" />
                                                </div>
                                              )}
                                              {editQuiz.quizType === 'assessment' && (
                                                <label className="flex items-center justify-center gap-1.5 md:w-20 cursor-pointer">
                                                  <input type="checkbox" checked={!!a.isCorrect} onChange={(e) => updateAnswer(q.id, a.id, { isCorrect: e.target.checked })} className="rounded text-success" />
                                                  <span className="text-xs text-ink-3 md:hidden">Correct answer</span>
                                                </label>
                                              )}
                                              {(editQuiz.quizType === 'personality' || editQuiz.quizType === 'outcome') && (
                                                <select value={a.outcomeId || ''} onChange={(e) => updateAnswer(q.id, a.id, { outcomeId: e.target.value || undefined })} className="ds-input w-full md:w-36 text-xs">
                                                  <option value="">— No result —</option>
                                                  {editQuiz.results.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
                                                </select>
                                              )}
                                              <button onClick={() => removeAnswer(q.id, a.id)} className="text-ink-3 hover:text-danger flex-shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                                              {isVideo && editQuiz.video && (
                                                <VideoAnswerFields
                                                  answer={a}
                                                  segments={editQuiz.video.segments}
                                                  followUps={followUpQuestions.filter((f) => f.id !== q.id)}
                                                  cfTags={cfTags}
                                                  onChange={(patch) => updateAnswer(q.id, a.id, patch)}
                                                />
                                              )}
                                            </div>
                                          ))}
                                          {q.type !== 'yes_no' && (
                                            <button onClick={() => addAnswer(q.id)} className="text-xs text-accent-ink hover:opacity-80">+ Add Answer</button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                </Fragment>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── Video Tab ── */}
                      {activeTab === 'video' && isVideo && (
                        <VideoTab
                          quiz={editQuiz}
                          onChange={(patch) => setEditQuiz((p) => (p ? { ...p, ...patch } : p))}
                          onSaveNow={handleSave}
                          cfTags={cfTags}
                          onTagCreated={(tag) => setCfTags((prev) => (prev.some((t) => t.id === tag.id) ? prev : [...prev, tag]))}
                          previewKey={previewKey}
                        />
                      )}

                      {/* ── Thank You Tab (surveys replace Results with this) ── */}
                      {activeTab === 'results' && isSurvey && (
                        <div className="max-w-xl space-y-4">
                          <p className="text-sm text-ink-3">
                            Surveys aren't scored, so there are no results — this is what the respondent sees after the last question.
                          </p>
                          <div>
                            <label className="block text-sm font-medium text-ink-2 mb-1">Headline</label>
                            <input
                              type="text"
                              value={editQuiz.settings.thankYou?.headline ?? ''}
                              onChange={(e) => setEditQuiz({ ...editQuiz, settings: { ...editQuiz.settings, thankYou: { ...editQuiz.settings.thankYou, headline: e.target.value } } })}
                              placeholder="Thank you!"
                              className="ds-input w-full"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-ink-2 mb-1">Message</label>
                            <textarea
                              value={editQuiz.settings.thankYou?.message ?? ''}
                              onChange={(e) => setEditQuiz({ ...editQuiz, settings: { ...editQuiz.settings, thankYou: { ...editQuiz.settings.thankYou, message: e.target.value } } })}
                              placeholder="Your response has been recorded."
                              rows={3}
                              className="ds-input w-full"
                            />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-ink-2 mb-1">Button text <span className="text-ink-3 font-normal">(optional)</span></label>
                              <input
                                type="text"
                                value={editQuiz.settings.thankYou?.ctaText ?? ''}
                                onChange={(e) => setEditQuiz({ ...editQuiz, settings: { ...editQuiz.settings, thankYou: { ...editQuiz.settings.thankYou, ctaText: e.target.value } } })}
                                placeholder="Back to site"
                                className="ds-input w-full"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-ink-2 mb-1">Button URL <span className="text-ink-3 font-normal">(optional)</span></label>
                              <input
                                type="url"
                                value={editQuiz.settings.thankYou?.ctaUrl ?? ''}
                                onChange={(e) => setEditQuiz({ ...editQuiz, settings: { ...editQuiz.settings, thankYou: { ...editQuiz.settings.thankYou, ctaUrl: e.target.value } } })}
                                placeholder="https://example.com"
                                className="ds-input w-full"
                              />
                            </div>
                          </div>
                          <div className="border-t border-line pt-4">
                            <label className="block text-sm font-medium text-ink-2 mb-1">Redirect instead <span className="text-ink-3 font-normal">(optional)</span></label>
                            <input
                              type="url"
                              value={editQuiz.settings.thankYou?.redirectUrl ?? ''}
                              onChange={(e) => setEditQuiz({ ...editQuiz, settings: { ...editQuiz.settings, thankYou: { ...editQuiz.settings.thankYou, redirectUrl: e.target.value } } })}
                              placeholder="https://example.com/thanks"
                              className="ds-input w-full"
                            />
                            <p className="text-xs text-ink-3 mt-1">
                              If set, the whole page redirects here on submit and the thank-you screen above is never shown.
                            </p>
                          </div>
                        </div>
                      )}

                      {/* ── Results Tab ── */}
                      {activeTab === 'results' && !isSurvey && (
                        <div>
                          <div className="flex justify-between items-center mb-4">
                            <p className="text-sm text-ink-3">Define the possible outcomes for your quiz.</p>
                            <button onClick={addResult} className="flex items-center gap-1 px-3 py-1.5 bg-accent-soft text-accent-ink rounded-[10px] text-sm font-medium hover:opacity-90">
                              <Plus className="w-4 h-4" /> Add Result
                            </button>
                          </div>
                          {editQuiz.results.length === 0 ? (
                            <p className="text-ink-3 text-sm text-center py-8">No results yet. Add at least one result outcome.</p>
                          ) : (
                            <div className="space-y-4">
                              {editQuiz.results.map((r) => (
                                <div key={r.id} className="border border-line rounded-lg p-4 bg-surface-2">
                                  <div className="flex justify-between items-start mb-3">
                                    <input type="text" value={r.title} onChange={(e) => updateResult(r.id, { title: e.target.value })} placeholder="Result title (e.g. The Innovator)" className="ds-input flex-1 font-medium" />
                                    <button onClick={() => removeResult(r.id)} className="ml-2 text-ink-3 hover:text-danger"><Trash2 className="w-4 h-4" /></button>
                                  </div>
                                  <textarea value={r.description} onChange={(e) => updateResult(r.id, { description: e.target.value })} placeholder="Description shown to quiz taker" rows={2} className="ds-input w-full mb-3" />
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="sm:col-span-2">
                                      <label className="text-xs text-ink-3 mb-1 block">Redirect URL (optional)</label>
                                      <input type="url" value={r.ctaUrl} onChange={(e) => updateResult(r.id, { ctaUrl: e.target.value })} placeholder="https://..." className="ds-input w-full" />
                                      <p className="text-xs text-ink-3 mt-1">{r.ctaUrl ? 'Takers with this result are redirected straight here (whole page). Leave blank to show the in-quiz result page.' : 'Leave blank to show the in-quiz result page (title + description). Add a URL to auto-redirect there instead.'}</p>
                                    </div>
                                    {(editQuiz.quizType === 'scored' || editQuiz.quizType === 'assessment') && (
                                      <>
                                        <div>
                                          <label className="text-xs text-ink-3 mb-1 block">{editQuiz.quizType === 'assessment' ? 'Min Correct' : 'Min Score'}</label>
                                          <input type="number" value={r.minScore ?? ''} onChange={(e) => updateResult(r.id, { minScore: e.target.value ? parseInt(e.target.value) : undefined })} className="ds-input w-full" />
                                        </div>
                                        <div>
                                          <label className="text-xs text-ink-3 mb-1 block">{editQuiz.quizType === 'assessment' ? 'Max Correct' : 'Max Score'}</label>
                                          <input type="number" value={r.maxScore ?? ''} onChange={(e) => updateResult(r.id, { maxScore: e.target.value ? parseInt(e.target.value) : undefined })} className="ds-input w-full" />
                                        </div>
                                      </>
                                    )}
                                    <div>
                                      <label className="text-xs text-ink-3 mb-1 block">Image URL (optional)</label>
                                      <input type="url" value={r.imageUrl || ''} onChange={(e) => updateResult(r.id, { imageUrl: e.target.value || undefined })} placeholder="https://..." className="ds-input w-full" />
                                    </div>
                                    <div className="sm:col-span-2">
                                      <label className="text-xs text-ink-3 mb-1 block">Apply Tags when this result wins</label>
                                      {(() => {
                                        const selected = r.tagIds ?? (r.tagId ? [r.tagId] : []);
                                        if (cfTags.length === 0) return <p className="text-xs text-ink-3">No tags yet — add one in the Settings tab.</p>;
                                        return (
                                          <div className="flex flex-wrap gap-1.5">
                                            {[...cfTags].sort((a, b) => a.name.localeCompare(b.name)).map((t) => {
                                              const ts = String(t.id);
                                              const on = selected.includes(ts);
                                              return (
                                                <button
                                                  key={t.id}
                                                  type="button"
                                                  onClick={() => {
                                                    const next = on ? selected.filter((x) => x !== ts) : [...selected, ts];
                                                    updateResult(r.id, { tagIds: next, tagId: undefined });
                                                  }}
                                                  className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${on ? 'bg-accent-soft border-accent text-accent-ink' : 'bg-surface border-line text-ink-3 hover:border-ink-3'}`}
                                                >
                                                  {t.name}
                                                </button>
                                              );
                                            })}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── Lead Capture Tab ── */}
                      {activeTab === 'lead' && (
                        <div className="space-y-6">
                          <div className="flex items-center gap-3">
                            <input type="checkbox" checked={editQuiz.leadCapture.enabled} onChange={(e) => setEditQuiz({ ...editQuiz, leadCapture: { ...editQuiz.leadCapture, enabled: e.target.checked } })} className="rounded" id="lc-enabled" />
                            <label htmlFor="lc-enabled" className="text-sm font-medium text-ink-2">Enable lead capture (collect email before showing results)</label>
                          </div>
                          {editQuiz.leadCapture.enabled && (
                            <>
                              {isVideo ? (
                                <p className="text-sm text-ink-3">For video quizzes, choose when the form appears (before the video or after the last question) on the Video tab.</p>
                              ) : (
                              <div>
                                <label className="block text-sm font-medium text-ink-2 mb-2">When to show</label>
                                <div className="flex gap-4">
                                  {['before_results', 'first_question'].map((pos) => (
                                    <label key={pos} className="flex items-center gap-2 text-sm">
                                      <input type="radio" name="lc-pos" checked={editQuiz.leadCapture.position === pos} onChange={() => setEditQuiz({ ...editQuiz, leadCapture: { ...editQuiz.leadCapture, position: pos as QuizLeadCapture['position'] } })} />
                                      {pos === 'before_results' ? 'Before showing results' : 'At the beginning'}
                                    </label>
                                  ))}
                                </div>
                              </div>
                              )}
                              <div>
                                <label className="block text-sm font-medium text-ink-2 mb-2">Fields to collect</label>
                                <div className="space-y-2">
                                  {(['email', 'firstName', 'lastName', 'phone'] as const).map((f) => (
                                    <label key={f} className="flex items-center gap-2 text-sm">
                                      <input type="checkbox" checked={editQuiz.leadCapture.fields[f]} disabled={f === 'email'}
                                        onChange={(e) => setEditQuiz({ ...editQuiz, leadCapture: { ...editQuiz.leadCapture, fields: { ...editQuiz.leadCapture.fields, [f]: e.target.checked } } })} className="rounded" />
                                      {f === 'email' ? 'Email (required)' : f === 'firstName' ? 'First Name' : f === 'lastName' ? 'Last Name' : 'Phone'}
                                    </label>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <label className="ds-label">Headline</label>
                                <input type="text" value={editQuiz.leadCapture.headline} onChange={(e) => setEditQuiz({ ...editQuiz, leadCapture: { ...editQuiz.leadCapture, headline: e.target.value } })} className="ds-input w-full" />
                              </div>
                              <div>
                                <label className="ds-label">Subtext</label>
                                <input type="text" value={editQuiz.leadCapture.subtext} onChange={(e) => setEditQuiz({ ...editQuiz, leadCapture: { ...editQuiz.leadCapture, subtext: e.target.value } })} className="ds-input w-full" />
                              </div>
                            </>
                          )}

                          <div className="border-t border-line pt-4">
                            <label className="block text-sm font-medium text-ink-2 mb-2">Global Tags (applied to all quiz takers)</label>
                            {loadingTags ? (
                              <div className="flex items-center gap-2 text-sm text-ink-3"><Spinner className="!h-4 !w-4" /> Loading tags...</div>
                            ) : cfTags.length === 0 ? (
                              <p className="text-sm text-ink-3">No tags yet. Add one below — reusable across all forms, quizzes &amp; buttons.</p>
                            ) : (
                              <div className="space-y-2 max-h-40 overflow-y-auto border border-line rounded-lg p-3">
                                {[...cfTags].sort((a, b) => a.name.localeCompare(b.name)).map((tag) => {
                                  const sel = (editQuiz.settings.tagIds || []).includes(String(tag.id));
                                  return (
                                    <label key={tag.id} className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer ${sel ? 'bg-accent-soft' : 'hover:bg-surface-2'}`}>
                                      <input type="checkbox" checked={sel} onChange={() => {
                                        const cur = editQuiz.settings.tagIds || [];
                                        const ts = String(tag.id);
                                        setEditQuiz({ ...editQuiz, settings: { ...editQuiz.settings, tagIds: sel ? cur.filter((t) => t !== ts) : [...cur, ts] } });
                                      }} className="rounded text-accent-ink" />
                                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color !== '#EEEEEE' ? tag.color : '#9CA3AF' }} />
                                      <span className="text-sm text-ink-2">{tag.name}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                            {!loadingTags && editQuiz.connectionId && (
                              <TagCreator
                                connectionId={editQuiz.connectionId}
                                onCreated={(tag) => {
                                  setCfTags((prev) => (prev.some((t) => t.id === tag.id) ? prev : [...prev, tag]));
                                  const cur = editQuiz.settings.tagIds || [];
                                  if (!cur.includes(String(tag.id))) setEditQuiz({ ...editQuiz, settings: { ...editQuiz.settings, tagIds: [...cur, String(tag.id)] } });
                                }}
                              />
                            )}
                          </div>

                          <div className="border-t border-line pt-4 grid grid-cols-2 gap-4">
                            <div>
                              <label className="ds-label">Result Attribute Key</label>
                              <input type="text" value={editQuiz.settings.resultAttributeKey || ''} onChange={(e) => setEditQuiz({ ...editQuiz, settings: { ...editQuiz.settings, resultAttributeKey: e.target.value } })} placeholder="quiz_result" className="ds-input w-full font-mono" />
                              <p className="text-xs text-ink-3 mt-1">CF custom attribute for the result name</p>
                            </div>
                            <div>
                              <label className="ds-label">Score Attribute Key</label>
                              <input type="text" value={editQuiz.settings.scoreAttributeKey || ''} onChange={(e) => setEditQuiz({ ...editQuiz, settings: { ...editQuiz.settings, scoreAttributeKey: e.target.value } })} placeholder="quiz_score" className="ds-input w-full font-mono" />
                              <p className="text-xs text-ink-3 mt-1">CF custom attribute for the numeric score</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* ── Style Tab ── */}
                      {activeTab === 'style' && isVideo && (
                        <VideoStyleTab
                          quizId={editQuiz.id}
                          style={editQuiz.styleConfig.video}
                          onChange={(video) => setEditQuiz((p) => (p ? { ...p, styleConfig: { ...p.styleConfig, video } } : p))}
                          previewKey={previewKey}
                        />
                      )}
                      {activeTab === 'style' && !isVideo && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div>
                            <label className="ds-label">Primary Color</label>
                            <div className="flex items-center gap-2">
                              <input type="color" value={editQuiz.styleConfig.primaryColor} onChange={(e) => setEditQuiz({ ...editQuiz, styleConfig: { ...editQuiz.styleConfig, primaryColor: e.target.value } })} className="h-9 w-12 rounded border border-line cursor-pointer" />
                              <input type="text" value={editQuiz.styleConfig.primaryColor} onChange={(e) => setEditQuiz({ ...editQuiz, styleConfig: { ...editQuiz.styleConfig, primaryColor: e.target.value } })} className="ds-input w-28" />
                            </div>
                          </div>
                          <div>
                            <label className="ds-label">Background Color</label>
                            <div className="flex items-center gap-2">
                              <input type="color" value={editQuiz.styleConfig.backgroundColor} onChange={(e) => setEditQuiz({ ...editQuiz, styleConfig: { ...editQuiz.styleConfig, backgroundColor: e.target.value } })} className="h-9 w-12 rounded border border-line cursor-pointer" />
                              <input type="text" value={editQuiz.styleConfig.backgroundColor} onChange={(e) => setEditQuiz({ ...editQuiz, styleConfig: { ...editQuiz.styleConfig, backgroundColor: e.target.value } })} className="ds-input w-28" />
                            </div>
                          </div>
                          <div>
                            <label className="ds-label">Background Transparency</label>
                            <div className="flex items-center gap-3">
                              <input type="range" min="0" max="100" value={editQuiz.styleConfig.backgroundOpacity ?? 100} onChange={(e) => setEditQuiz({ ...editQuiz, styleConfig: { ...editQuiz.styleConfig, backgroundOpacity: Number(e.target.value) } })} className="flex-1 h-2 accent-[var(--accent)]" />
                              <span className="text-sm text-ink-3 w-10 text-right">{editQuiz.styleConfig.backgroundOpacity ?? 100}%</span>
                            </div>
                          </div>
                          <div>
                            <label className="ds-label">Text Color</label>
                            <div className="flex items-center gap-2">
                              <input type="color" value={editQuiz.styleConfig.textColor} onChange={(e) => setEditQuiz({ ...editQuiz, styleConfig: { ...editQuiz.styleConfig, textColor: e.target.value } })} className="h-9 w-12 rounded border border-line cursor-pointer" />
                              <input type="text" value={editQuiz.styleConfig.textColor} onChange={(e) => setEditQuiz({ ...editQuiz, styleConfig: { ...editQuiz.styleConfig, textColor: e.target.value } })} className="ds-input w-28" />
                            </div>
                          </div>
                          <div>
                            <label className="ds-label">Font Family</label>
                            <select value={editQuiz.styleConfig.fontFamily} onChange={(e) => setEditQuiz({ ...editQuiz, styleConfig: { ...editQuiz.styleConfig, fontFamily: e.target.value } })} className="ds-input w-full">
                              {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="ds-label">Button Text</label>
                            <input type="text" value={editQuiz.styleConfig.buttonText} onChange={(e) => setEditQuiz({ ...editQuiz, styleConfig: { ...editQuiz.styleConfig, buttonText: e.target.value } })} className="ds-input w-full" />
                          </div>
                          <div>
                            <label className="ds-label">Border Radius</label>
                            <input type="text" value={editQuiz.styleConfig.borderRadius} onChange={(e) => setEditQuiz({ ...editQuiz, styleConfig: { ...editQuiz.styleConfig, borderRadius: e.target.value } })} className="ds-input w-full" />
                          </div>
                          <div>
                            <label className="ds-label">Quiz Width</label>
                            <input type="text" value={editQuiz.styleConfig.quizWidth} onChange={(e) => setEditQuiz({ ...editQuiz, styleConfig: { ...editQuiz.styleConfig, quizWidth: e.target.value } })} className="ds-input w-full" />
                          </div>
                          <div>
                            <label className="ds-label">Input Background</label>
                            <div className="flex items-center gap-2">
                              <input type="color" value={editQuiz.styleConfig.inputBackgroundColor || '#FFFFFF'} onChange={(e) => setEditQuiz({ ...editQuiz, styleConfig: { ...editQuiz.styleConfig, inputBackgroundColor: e.target.value } })} className="h-9 w-12 rounded border border-line cursor-pointer" />
                              <input type="text" value={editQuiz.styleConfig.inputBackgroundColor || '#FFFFFF'} onChange={(e) => setEditQuiz({ ...editQuiz, styleConfig: { ...editQuiz.styleConfig, inputBackgroundColor: e.target.value } })} className="ds-input w-28" />
                            </div>
                          </div>
                          <div>
                            <label className="ds-label">Input Border Color</label>
                            <div className="flex items-center gap-2">
                              <input type="color" value={editQuiz.styleConfig.inputBorderColor || '#E5E7EB'} onChange={(e) => setEditQuiz({ ...editQuiz, styleConfig: { ...editQuiz.styleConfig, inputBorderColor: e.target.value } })} className="h-9 w-12 rounded border border-line cursor-pointer" />
                              <input type="text" value={editQuiz.styleConfig.inputBorderColor || '#E5E7EB'} onChange={(e) => setEditQuiz({ ...editQuiz, styleConfig: { ...editQuiz.styleConfig, inputBorderColor: e.target.value } })} className="ds-input w-28" />
                            </div>
                          </div>
                          <div className="space-y-3 pt-4">
                            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editQuiz.styleConfig.showProgressBar} onChange={(e) => setEditQuiz({ ...editQuiz, styleConfig: { ...editQuiz.styleConfig, showProgressBar: e.target.checked } })} className="rounded" /> Show progress bar</label>
                            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editQuiz.styleConfig.showQuestionNumbers} onChange={(e) => setEditQuiz({ ...editQuiz, styleConfig: { ...editQuiz.styleConfig, showQuestionNumbers: e.target.checked } })} className="rounded" /> Show question numbers</label>
                          </div>
                        </div>
                      )}

                      {/* ── Embed Tab ── */}
                      {activeTab === 'embed' && isVideo && (
                        <VideoEmbedPanel slug={editQuiz.slug} published={editQuiz.status === 'published'} driver={editQuiz.video?.driver ?? 'native'} />
                      )}
                      {activeTab === 'embed' && !isVideo && (
                        <div className="space-y-6">
                          {editQuiz.status !== 'published' && (
                            <div className="p-3 bg-warning-soft border border-line rounded-lg text-warning text-sm flex items-center gap-2">
                              <AlertCircle className="w-4 h-4" /> Publish this quiz to generate embed code and preview it.
                            </div>
                          )}
                          <div>
                            <label className="block text-sm font-medium text-ink-2 mb-2">Direct Link</label>
                            <div className="flex items-center gap-2">
                              <code className="flex-1 bg-surface-2 px-3 py-2 rounded text-sm font-mono text-ink truncate">{quizzesApi.getQuizUrl(editQuiz.slug)}</code>
                              <button onClick={() => { navigator.clipboard.writeText(quizzesApi.getQuizUrl(editQuiz.slug)); setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000); }} className="p-2 text-ink-3 hover:text-accent-ink">
                                {copiedLink ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
                              </button>
                              {editQuiz.status === 'published' && <a href={quizzesApi.getQuizUrl(editQuiz.slug)} target="_blank" rel="noopener noreferrer" className="p-2 text-ink-3 hover:text-accent-ink"><ExternalLink className="w-4 h-4" /></a>}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-ink-2 mb-2">Widget Script (for CF pages)</label>
                            <div className="relative">
                              <pre className="bg-[var(--code-bg)] text-[var(--code-ink)] border border-line rounded-[11px] p-3.5 font-mono text-[12px] leading-relaxed overflow-auto">{quizzesApi.getWidgetCode(editQuiz.slug)}</pre>
                              <button onClick={() => { navigator.clipboard.writeText(quizzesApi.getWidgetCode(editQuiz.slug)); setCopiedEmbed(true); setTimeout(() => setCopiedEmbed(false), 2000); }} className="absolute top-2 right-2 p-2 bg-surface-2 rounded text-ink-3 hover:text-ink">
                                {copiedEmbed ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-ink-2 mb-2">Iframe Embed</label>
                            <div className="relative">
                              <pre className="bg-[var(--code-bg)] text-[var(--code-ink)] border border-line rounded-[11px] p-3.5 font-mono text-[12px] leading-relaxed overflow-auto">{quizzesApi.getEmbedCode(editQuiz.slug)}</pre>
                              <button onClick={() => { navigator.clipboard.writeText(quizzesApi.getEmbedCode(editQuiz.slug)); setCopiedEmbed(true); setTimeout(() => setCopiedEmbed(false), 2000); }} className="absolute top-2 right-2 p-2 bg-surface-2 rounded text-ink-3 hover:text-ink">
                                {copiedEmbed ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          {editQuiz.status === 'published' && (
                            <div>
                              <label className="block text-sm font-medium text-ink-2 mb-2">Preview</label>
                              <div className="border border-line rounded-lg overflow-hidden bg-surface-2">
                                <iframe src={quizzesApi.getQuizUrl(editQuiz.slug)} width="100%" height="600" style={{ border: 'none' }} title="Quiz Preview" />
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── Submissions Tab ── */}
                      {activeTab === 'submissions' && !isVideo && (
                        <SubmissionsPanel kind="quiz" resourceId={editQuiz.id} />
                      )}
                      {activeTab === 'submissions' && isVideo && (
                        <VideoInsightsPanel quizId={editQuiz.id} questions={editQuiz.questions} />
                      )}

                      {/* Save button */}
                      {['questions', 'video', 'results', 'lead', 'style'].includes(activeTab) && (
                        <div className="mt-6 flex flex-col items-end gap-2">
                          {saveError && (
                            <div className="w-full p-3 bg-danger-soft border border-line rounded-[11px] flex items-center gap-2 text-danger text-sm">
                              <AlertCircle className="w-4 h-4 flex-shrink-0" /> <ErrorMessage message={saveError} />
                              <button onClick={() => setSaveError(null)} className="ml-auto text-ink-3 hover:text-danger"><X className="w-4 h-4" /></button>
                            </div>
                          )}
                          <div className="flex items-center gap-3">
                            {savedMsg && <span className="text-xs text-success flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Saved</span>}
                            <Button onClick={() => handleSave()} disabled={saving}>
                              {saving ? 'Saving...' : 'Save Changes'}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="ds-card shadow-card p-4 md:p-6 w-full max-w-lg">
              <h2 className="font-display text-lg font-semibold mb-4 text-ink">Create New Quiz</h2>
              <div className="space-y-4">
                <div>
                  <label className="ds-label">Quiz Name</label>
                  <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. What Type of Leader Are You?" className="ds-input w-full" autoFocus />
                </div>
                <div>
                  <label className="ds-label">ClickFunnels Connection</label>
                  {connections.length === 0 ? (
                    <p className="text-sm text-warning">No active ClickFunnels connections. Add one in Vault &gt; Connections first.</p>
                  ) : (
                    <select value={newConnId} onChange={(e) => setNewConnId(e.target.value)} className="ds-input w-full">
                      <option value="">Select connection...</option>
                      {connections.map((c) => <option key={c.id} value={c.id}>{c.displayName || c.workspaceName || c.platform}</option>)}
                    </select>
                  )}
                </div>
                <div className="flex items-start gap-3 p-3 border border-line rounded-lg">
                  <Switch checked={newVideo} onChange={setNewVideo} label="Video quiz" />
                  <div>
                    <div className="text-sm font-medium text-ink flex items-center gap-1.5"><Film className="w-4 h-4" /> Video quiz</div>
                    <div className="text-xs text-ink-3 mt-0.5">Questions pop up at set moments in your video, and answers can play different clips. Available on Starter and up.</div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-2 mb-2">{newVideo ? 'Scoring' : 'Quiz Type'}</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {QUIZ_TYPE_INFO.map((t) => (
                      <button key={t.value} onClick={() => setNewType(t.value)}
                        className={`p-3 rounded-lg border-2 text-left transition-colors ${newType === t.value ? 'border-accent bg-accent-soft' : 'border-line hover:border-ink-3'}`}>
                        <div className="text-sm font-medium text-ink">{t.label}</div>
                        <div className="text-xs text-ink-3 mt-1">{t.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              {createError && (
                <div className="mt-4 p-3 bg-danger-soft border border-line rounded-[11px] flex items-center gap-2 text-danger text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" /> <ErrorMessage message={createError} />
                </div>
              )}
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="ghost" onClick={() => { setShowCreateModal(false); setNewName(''); setNewConnId(''); setNewVideo(false); setCreateError(null); }}>Cancel</Button>
                <Button onClick={handleCreate} disabled={creating || !newName.trim() || !newConnId}>
                  {creating ? 'Creating...' : 'Create Quiz'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Import Modal */}
        {showImportModal && editQuiz && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="ds-card shadow-card w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-4 md:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="font-display text-lg font-semibold text-ink">Import Quiz Data</h2>
                  <button onClick={() => setShowImportModal(false)} className="text-ink-3 hover:text-ink"><X className="w-5 h-5" /></button>
                </div>

                {/* AI Prompt Template */}
                <div className="bg-accent-soft border border-line rounded-lg p-4 mb-4">
                  <p className="text-sm font-medium text-accent-ink mb-2">AI Prompt Template</p>
                  <p className="text-xs text-accent-ink mb-3">Copy this prompt and give it to ChatGPT, Claude, or any AI to generate your quiz content:</p>
                  <pre className="bg-[var(--code-bg)] text-[var(--code-ink)] border border-line rounded-[11px] p-3.5 font-mono text-[12px] leading-relaxed whitespace-pre-wrap max-h-40 overflow-auto">{getAIPrompt()}</pre>
                  <button
                    onClick={() => { navigator.clipboard.writeText(getAIPrompt()); setCopiedPrompt(true); setTimeout(() => setCopiedPrompt(false), 2000); }}
                    className="mt-2 flex items-center gap-1 px-3 py-1.5 bg-accent text-white rounded text-xs font-medium hover:opacity-90"
                  >
                    {copiedPrompt ? <><Check className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy Prompt</>}
                  </button>
                </div>

                {/* JSON Input */}
                <div>
                  <label className="ds-label">Paste your quiz JSON below</label>
                  <textarea
                    value={importJson}
                    onChange={(e) => { setImportJson(e.target.value); setImportError(null); }}
                    rows={12}
                    className="ds-input w-full font-mono resize-y"
                    placeholder={'{\n  "questions": [\n    {\n      "type": "single_choice",\n      "title": "Your question here",\n      "answers": [\n        { "text": "Answer A", "score": 1, "outcome": "Result A" }\n      ]\n    }\n  ],\n  "results": [\n    {\n      "title": "Result A",\n      "description": "Description here"\n    }\n  ]\n}'}
                  />
                </div>

                {importError && (
                  <div className="mt-2 p-2 bg-danger-soft border border-line rounded text-danger text-xs flex items-center gap-2">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> {importError}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4">
                  <Button variant="ghost" onClick={() => setShowImportModal(false)}>Cancel</Button>
                  <Button variant="ghost" onClick={() => processImport('append')} disabled={!importJson.trim()}>
                    Import &amp; Append
                  </Button>
                  <Button onClick={() => processImport('replace')} disabled={!importJson.trim()}>
                    Import &amp; Replace
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
